import { yupResolver } from "@hookform/resolvers/yup";
import { HeadingTag, VideoSectionContent } from "api/shop";
import { CourseGroupAttributeType, useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import {
  IDropdownOption,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
} from "components/atoms";
import { PaziaDropdown } from "components/atoms/PaziaDropdown/PaziaDropdown";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { HEADING_TAG_OPTIONS } from "components/atoms/HeadingTagSelect/HeadingTagSelect";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import * as yup from "yup";

interface VideoSectionFormProps {
  onSubmit: (data: VideoSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: VideoSectionContent;
  isLoading?: boolean;
}

const HEADER_ALIGN_OPTIONS: IDropdownOption[] = [
  { value: "left", label: "Lewo" },
  { value: "center", label: "Środek" },
  { value: "right", label: "Prawo" },
];

const DESCRIPTION_POSITION_OPTIONS: IDropdownOption[] = [
  { value: "top", label: "Góra" },
  { value: "bottom", label: "Dół" },
  { value: "left", label: "Lewo" },
  { value: "right", label: "Prawo" },
];

export const VideoSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: VideoSectionFormProps) => {
  const { allAttributes } = useCourseGroupAttributesAll();
  const textAttributes = allAttributes.filter((a) => a.attributeType === CourseGroupAttributeType.Text);
  const wysiwygAttributes = allAttributes.filter((a) => a.attributeType === CourseGroupAttributeType.Wysiwyg);
  const textAttributeOptions = textAttributes.map((a) => ({ value: a.id, label: a.name }));
  const wysiwygAttributeOptions = wysiwygAttributes.map((a) => ({ value: a.id, label: a.name }));

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      videoUrl: defaultValues?.videoUrl ?? "",
      videoSourceMode: defaultValues?.videoUrlAttributeId != null ? "attribute" : "manual",
      videoUrlAttributeId: defaultValues?.videoUrlAttributeId ?? null,
      headerText: defaultValues?.header?.text ?? "",
      headerTag: defaultValues?.header?.tag ?? "h2",
      headerAlign: defaultValues?.header?.align ?? "left",
      description: defaultValues?.description ?? "",
      descriptionSourceMode: defaultValues?.descriptionAttributeId != null ? "attribute" : "manual",
      descriptionAttributeId: defaultValues?.descriptionAttributeId ?? null,
      descriptionPosition: defaultValues?.descriptionPosition ?? "bottom",
    },
  });

  const { handleSubmit, control, setValue } = methods;

  const videoSourceMode = useWatch({ control, name: "videoSourceMode" });
  const descriptionSourceMode = useWatch({ control, name: "descriptionSourceMode" });

  const handleFormSubmit = (data: FormValues) => {
    const headerText = data.headerText?.trim() ?? "";
    const descriptionText = data.description?.trim() ?? "";
    const isVideoAttributeMode = data.videoSourceMode === "attribute";
    const isDescriptionAttributeMode = data.descriptionSourceMode === "attribute";

    const hasDescriptionContent =
      (!isDescriptionAttributeMode && !!descriptionText) ||
      (isDescriptionAttributeMode && data.descriptionAttributeId != null);

    const formattedData: VideoSectionFormValues = {
      videoUrl: isVideoAttributeMode && data.videoUrlAttributeId != null ? "" : data.videoUrl,
      videoUrlAttributeId:
        isVideoAttributeMode && data.videoUrlAttributeId != null ? data.videoUrlAttributeId : undefined,
      header: headerText
        ? {
            text: headerText,
            tag: (data.headerTag as HeadingTag) || undefined,
            align: (data.headerAlign as "left" | "center" | "right") || undefined,
          }
        : undefined,
      description: isDescriptionAttributeMode ? "" : descriptionText ? descriptionText : undefined,
      descriptionAttributeId:
        isDescriptionAttributeMode && data.descriptionAttributeId != null
          ? data.descriptionAttributeId
          : undefined,
      descriptionPosition: hasDescriptionContent
        ? (data.descriptionPosition as "top" | "bottom" | "left" | "right")
        : undefined,
    };
    onSubmit(formattedData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Źródło filmu</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setValue("videoSourceMode", "manual")}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  videoSourceMode === "manual"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Ręcznie
              </button>
              <button
                type="button"
                onClick={() => setValue("videoSourceMode", "attribute")}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  videoSourceMode === "attribute"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Atrybut jako źródło
              </button>
            </div>
          </div>

          {videoSourceMode === "manual" ? (
            <PaziaInput
              name="videoUrl"
              label="Link do filmu YouTube"
              required
              placeholder="https://www.youtube.com/watch?v=..."
            />
          ) : (
            <>
              {textAttributes.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Brak atrybutów tekstowych</p>
              ) : (
                <PaziaSelect
                  name="videoUrlAttributeId"
                  label="Atrybut tekstowy"
                  options={textAttributeOptions}
                />
              )}
            </>
          )}
        </div>

        <PaziaInput name="headerText" label="Nagłówek (opcjonalnie)" />

        <div className="grid gap-3 md:grid-cols-2">
          <PaziaDropdown
            name="headerTag"
            label="Tag nagłówka"
            options={HEADING_TAG_OPTIONS as IDropdownOption[]}
          />
          <PaziaDropdown
            name="headerAlign"
            label="Wyrównanie nagłówka"
            options={HEADER_ALIGN_OPTIONS}
          />
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Źródło opisu</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setValue("descriptionSourceMode", "manual")}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  descriptionSourceMode === "manual"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Ręcznie
              </button>
              <button
                type="button"
                onClick={() => setValue("descriptionSourceMode", "attribute")}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  descriptionSourceMode === "attribute"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Atrybut jako źródło
              </button>
            </div>
          </div>

          {descriptionSourceMode === "manual" ? (
            <PaziaFromQuill name="description" label="Opis (opcjonalnie)" />
          ) : (
            <>
              {wysiwygAttributes.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Brak atrybutów WYSIWYG</p>
              ) : (
                <PaziaSelect
                  name="descriptionAttributeId"
                  label="Atrybut WYSIWYG"
                  options={wysiwygAttributeOptions}
                />
              )}
            </>
          )}
        </div>

        <PaziaDropdown
          name="descriptionPosition"
          label="Pozycja opisu"
          options={DESCRIPTION_POSITION_OPTIONS}
        />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const schema = yup
  .object({
    videoSourceMode: yup.string().required(),
    videoUrl: yup.string().when("videoSourceMode", {
      is: "manual",
      then: (s) => s.required("Link do filmu jest wymagany"),
      otherwise: (s) => s.optional(),
    }),
    videoUrlAttributeId: yup.number().nullable(),
    headerText: yup.string().optional(),
    headerTag: yup.string().optional(),
    headerAlign: yup.string().optional(),
    description: yup.string().optional(),
    descriptionSourceMode: yup.string().required(),
    descriptionAttributeId: yup.number().nullable(),
    descriptionPosition: yup.string().optional(),
  })
  .required();

interface FormValues {
  videoUrl: string;
  videoSourceMode: string;
  videoUrlAttributeId: number | null;
  headerText?: string;
  headerTag?: string;
  headerAlign?: string;
  description?: string;
  descriptionSourceMode: string;
  descriptionAttributeId: number | null;
  descriptionPosition?: string;
}

export interface VideoSectionFormValues {
  videoUrl: string;
  videoUrlAttributeId?: number;
  header?: {
    text: string;
    tag?: HeadingTag;
    align?: "left" | "center" | "right";
  };
  description?: string;
  descriptionAttributeId?: number;
  descriptionPosition?: "top" | "bottom" | "left" | "right";
}
